import { useQueryClient } from "@tanstack/react-query"

import {
  TPatchUserNotificationSettingsResponse,
  TUserNotificationsSettingsResponse,
} from "src/data/user/types/userTypes"
import { TUser } from "src/data/user/user"
import { ErrorService } from "src/utils/ErrorService"

export const userKeys = {
  /** Use to target *all* user data in the cache, with fuzzy matching; matching this
   * way is ineffective and should be used with care */
  all() {
    return ["user"] as const
  },

  /** Use to target specific user data in cache */
  user(id: string) {
    return [...this.all(), id] as const
  },

  invitations(userId: string) {
    return [...this.user(userId), "invitations"] as const
  },

  invitation(userId: string, invitationCode: string) {
    return [...this.user(userId), "invitation", invitationCode] as const
  },

  invitationInfo(inviteCode: string) {
    return [...this.all(), "invitation-info", inviteCode] as const
  },

  notificationSettings(userId: string) {
    return [...this.user(userId), "notification-settings"] as const
  },
}

export function useUserCache() {
  const queryClient = useQueryClient()

  function invalidateUserFuzzy(userId: string) {
    return queryClient.invalidateQueries({
      queryKey: userKeys.user(userId),
    })
  }

  function updateCachedUser(
    userId: string,
    updater: (user: TUser | undefined) => TUser | undefined
  ) {
    queryClient.setQueryData<TUser>(userKeys.user(userId), updater)
  }

  function updateCachedNotificationSettings(
    userId: string,
    data: TPatchUserNotificationSettingsResponse
  ) {
    queryClient.setQueryData<TUserNotificationsSettingsResponse>(
      userKeys.notificationSettings(userId),
      (prev) => {
        if (!prev) {
          return data
        }

        const updatedEventTypes = data.events.map((e) => e.type)

        const newEvents = prev.events.map((old) => {
          if (updatedEventTypes.includes(old.type)) {
            const updatedEvent = data.events.find((e) => e.type === old.type)

            if (!updatedEvent) {
              ErrorService.captureMessage(
                "User notification settings update failed: updated event not found",
                { context: { data, prev } }
              )
              return old
            }

            return updatedEvent
          }

          return old
        })

        return { ...prev, events: newEvents }
      }
    )
  }

  return {
    invalidateUserFuzzy,
    updateCachedUser,
    updateCachedNotificationSettings,
  }
}
